import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store";
import {
  setFilters,
  clearFilters,
  type ForexFilters,
} from "@/store/slices/forexSlice";
import { forexApi } from "@/mocks/forexApi";

// Hook for forex state
export const useForexState = () => {
  return useSelector((state: RootState) => state.forex);
};

// Hook for vendors
export const useVendors = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "vendors"],
    queryFn: () => forexApi.getVendors(),
  });

  return {
    vendors: data || [],
    isLoading,
    error,
  };
};

// Hook for vendor by ID
export const useVendor = (id: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "vendor", id],
    queryFn: () => forexApi.getVendorById(id),
    enabled: !!id,
  });

  return {
    vendor: data,
    isLoading,
    error,
  };
};

// Hook for listings with filters
export const useListings = (filters?: Partial<ForexFilters>) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "listings", filters],
    queryFn: () => forexApi.getListings(filters),
  });

  return {
    listings: data || [],
    isLoading,
    error,
  };
};

// Hook for featured listings
export const useFeaturedListings = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "featured-listings"],
    queryFn: () => forexApi.getFeaturedListings(),
  });

  return {
    featuredListings: data || [],
    isLoading,
    error,
  };
};

// Hook for listing by ID
export const useListing = (id: string) => {
  const {
    data: listing,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["forex", "listing", id],
    queryFn: () => forexApi.getListingById(id),
    enabled: !!id,
  });

  // Record view mutation
  const recordView = useCallback(async () => {
    if (id) {
      await forexApi.recordView(id);
    }
  }, [id]);

  return {
    listing,
    isLoading,
    error,
    recordView,
  };
};

// Hook for vendor listings
export const useVendorListings = (vendorId: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "vendor-listings", vendorId],
    queryFn: () => forexApi.getVendorListings(vendorId),
    enabled: !!vendorId,
  });

  return {
    listings: data || [],
    isLoading,
    error,
  };
};

// Hook for listing reviews
export const useListingReviews = (listingId: string) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "reviews", listingId],
    queryFn: () => forexApi.getListingReviews(listingId),
    enabled: !!listingId,
  });

  // Submit review mutation
  const submitReviewMutation = useMutation({
    mutationFn: (reviewData: Parameters<typeof forexApi.submitReview>[0]) =>
      forexApi.submitReview(reviewData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["forex", "reviews", listingId],
      });
    },
  });

  return {
    reviews: data || [],
    isLoading,
    error,
    submitReview: submitReviewMutation.mutate,
    isSubmitting: submitReviewMutation.isPending,
  };
};

// Hook for creating listing
export const useCreateListing = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof forexApi.createListing>[0]) =>
      forexApi.createListing(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forex", "listings"] });
      queryClient.invalidateQueries({ queryKey: ["forex", "vendor-listings"] });
    },
  });

  return {
    createListing: mutation.mutate,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
};

// Hook for updating listing
export const useUpdateListing = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof forexApi.updateListing>[1];
    }) => forexApi.updateListing(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["forex", "listings"] });
      queryClient.invalidateQueries({
        queryKey: ["forex", "listing", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["forex", "vendor-listings"] });
    },
  });

  return {
    updateListing: mutation.mutate,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
};

// Hook for deleting listing
export const useDeleteListing = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) => forexApi.deleteListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forex", "listings"] });
      queryClient.invalidateQueries({ queryKey: ["forex", "vendor-listings"] });
    },
  });

  return {
    deleteListing: mutation.mutate,
    isDeleting: mutation.isPending,
    error: mutation.error,
  };
};

// Hook for recording clicks
export const useRecordClick = () => {
  const mutation = useMutation({
    mutationFn: (id: string) => forexApi.recordClick(id),
  });

  return {
    recordClick: mutation.mutate,
    isRecording: mutation.isPending,
  };
};

// Hook for reporting listing
export const useReportListing = () => {
  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof forexApi.reportListing>[0]) =>
      forexApi.reportListing(data),
  });

  return {
    reportListing: mutation.mutate,
    isReporting: mutation.isPending,
    error: mutation.error,
  };
};

// Hook for vendor dashboard stats
export const useVendorDashboardStats = (vendorId: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "dashboard-stats", vendorId],
    queryFn: () => forexApi.getVendorDashboardStats(vendorId),
    enabled: !!vendorId,
  });

  return {
    stats: data,
    isLoading,
    error,
  };
};

// Hook for location search
export const useLocationSearch = (query: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["forex", "locations", query],
    queryFn: () => forexApi.searchLocations(query),
    enabled: query.length >= 2,
  });

  return {
    locations: data || [],
    isLoading,
  };
};

// Hook for nearby listings
export const useNearbyListings = (
  lat: number,
  lng: number,
  radius?: number,
) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["forex", "nearby", lat, lng, radius],
    queryFn: () => forexApi.getNearbyListings(lat, lng, radius),
    enabled: !!lat && !!lng,
  });

  return {
    listings: data || [],
    isLoading,
    error,
  };
};

// Hook for filters
export const useForexFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.forex.filters);

  const updateFilters = useCallback(
    (newFilters: Partial<ForexFilters>) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );

  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  return {
    filters,
    updateFilters,
    resetFilters,
  };
};

// Admin hooks
export const useForexAdmin = () => {
  const queryClient = useQueryClient();

  // Get all reports
  const { data: reports, isLoading: isLoadingReports } = useQuery({
    queryKey: ["forex", "admin", "reports"],
    queryFn: () => forexApi.getReports(),
  });

  // Approve listing mutation
  const approveListing = useMutation({
    mutationFn: (id: string) => forexApi.approveListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forex", "listings"] });
    },
  });

  // Reject listing mutation
  const rejectListing = useMutation({
    mutationFn: (id: string) => forexApi.rejectListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forex", "listings"] });
    },
  });

  // Verify vendor mutation
  const verifyVendor = useMutation({
    mutationFn: ({ id, badge }: { id: string; badge: "basic" | "premium" }) =>
      forexApi.verifyVendor(id, badge),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forex", "vendors"] });
    },
  });

  // Suspend vendor mutation
  const suspendVendor = useMutation({
    mutationFn: (id: string) => forexApi.suspendVendor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forex", "vendors"] });
    },
  });

  // Update report status
  const updateReportStatus = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "pending" | "investigating" | "resolved" | "dismissed";
    }) => forexApi.updateReportStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["forex", "admin", "reports"],
      });
    },
  });

  return {
    reports: reports || [],
    isLoadingReports,
    approveListing: approveListing.mutate,
    rejectListing: rejectListing.mutate,
    verifyVendor: verifyVendor.mutate,
    suspendVendor: suspendVendor.mutate,
    updateReportStatus: updateReportStatus.mutate,
  };
};
